import { AccountConnectScreen } from "@/features/accounts/AccountConnectScreen";
import { AccountConnectModal } from "@/features/accounts/AccountConnectModal";
import { MailAccountSettingsModal } from "@/features/accounts/MailAccountSettingsModal";
import { ComposeDrawer } from "@/features/compose/ComposeDrawer";
import { MailMoveFolderDialog } from "@/features/shared/MailMoveFolderDialog";
import { SnoozeModal } from "@/features/snooze/SnoozeModal";
import { CoffreDestFolderPickerModal } from "@/features/message/CoffreDestFolderPickerModal";
import { BulkAttachmentProgressModal } from "@/features/message/BulkAttachmentProgressModal";
import { MobileComposeFab } from "@/mobile/components/compose/MobileComposeFab";
import { MobileAppShell } from "@/mobile/components/shell/MobileAppShell";
import { MobileNavDrawer } from "@/mobile/components/nav/MobileNavDrawer";
import { MobileReaderOverlay } from "@/mobile/components/message/MobileReaderOverlay";
import { MailFolderStorageDialog } from "@/features/folders/MailFolderStorageDialog";
import { notifyMailStorageStatsRefresh } from "@/features/accounts/MailStorageQuotaSection";
import { flattenFoldersForPicker } from "@/lib/folders/folderTree";
import { isDraftsNavFolder } from "@/lib/mail/folderNavSections";
import { useMailAppBoot } from "@/hooks/useMailAppBoot";
import { MailBootScreen } from "@/features/boot/MailBootScreen";
import { useMobileMailApp } from "@/mobile/hooks/useMobileMailApp";
import { MobileInboxScreen } from "@/mobile/screens/MobileInboxScreen";
import { MobileReaderScreen } from "@/mobile/screens/MobileReaderScreen";

/** Shell mobile — navigation stack, composants légers, isolé du workspace desktop. */
export function MobileMailApp() {
  const app = useMobileMailApp();
  const boot = useMailAppBoot({
    accountsLoading: app.mail.accountsLoading,
    accountsError: app.mail.accountsError,
    hasAccount: app.mail.hasAccount,
    accountsHasList: app.mail.accounts.length > 0 || (!app.mail.accountsLoading && app.mail.accountsError == null),
    accountId: app.mail.accountId ?? undefined,
    foldersLoading: app.mail.foldersLoading,
    foldersHasList: app.mail.folders.length > 0,
    inboxListSettled: app.mail.listSettled === true,
  });
  const bootOverlay = boot.showOverlay ? (
    <MailBootScreen
      label={boot.label}
      mode="loading"
      exiting={boot.exiting}
      optimization={boot.optimization}
      onSkipBackground={boot.skipToBackground}
    />
  ) : null;
  const folderMeta = flattenFoldersForPicker(app.mail.folders).find(
    (f) => f.name === app.mail.folder
  );
  const folderStorageEnabled =
    !app.nav.readerOpen &&
    !app.isVirtualListView &&
    app.mail.accountId != null &&
    !app.isDraftsFolder &&
    !(folderMeta != null && isDraftsNavFolder(folderMeta.name, folderMeta.label));

  if (!boot.ready && !boot.exiting) {
    return bootOverlay;
  }

  if (app.mail.accountsError && app.mail.accounts.length === 0) {
    return (
      <>
        {bootOverlay}
        <MobileAppShell>
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 text-center">
          <p className="text-sm text-[var(--mail-muted)]">{app.mail.accountsError}</p>
          <button
            type="button"
            className="rounded-lg bg-[var(--mail-accent)] px-4 py-2 text-sm font-medium text-white"
            onClick={() => void app.mail.reloadAccounts()}
          >
            Réessayer
          </button>
        </div>
        </MobileAppShell>
      </>
    );
  }

  if (!app.mail.hasAccount || !app.mail.currentAccount) {
    return (
      <>
        {bootOverlay}
        <MobileAppShell>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <AccountConnectScreen onConnected={() => void app.mail.reloadAccounts()} />
        </div>
        </MobileAppShell>
      </>
    );
  }

  const closeReader = () => {
    app.nav.backToInbox();
    app.mail.syncInBackground();
  };

  const readerAction = (actionId: string) => {
    if (app.nav.selectedUid == null) return;
    if (
      actionId === "markRead" ||
      actionId === "markUnread" ||
      app.bulk.selectionCount > 0
    ) {
      void app.bulk.handleMessageAction(actionId);
      return;
    }
    void app.actions.handleReaderAction(app.nav.selectedUid, actionId);
  };

  return (
    <MobileAppShell>
      <div
        className={`min-h-0 flex-1${app.nav.readerOpen ? " pointer-events-none" : ""}`}
        aria-hidden={app.nav.readerOpen}
      >
        <MobileInboxScreen
          account={app.mail.currentAccount}
          accountId={app.mail.accountId}
          folder={app.mail.folder}
          folderTitle={app.folderTitle}
          messages={app.inboxListMessages}
          loadingInitial={!app.isVirtualListView && app.mail.loadingInitial}
          syncing={!app.isVirtualListView && app.mail.inboxSyncing}
          refreshing={app.mail.refreshing}
          error={app.mail.error}
          hasMore={!app.isVirtualListView && !app.listFilter.unreadOnly && app.mail.hasMore}
          loadingMore={app.mail.loadingMore}
          onLoadMore={() => void app.mail.loadMore()}
          listFilter={app.listFilter}
          onListFilterChange={app.handleListFilterChange}
          searchInput={app.searchInput}
          onSearchInputChange={app.setSearchInput}
          searchDisabled={app.isVirtualListView}
          searchEmpty={app.searchDisplay.showEmptyResults}
          searchImapLoading={app.searchDisplay.imapSearchLoading}
          searchDebouncePending={app.searchDisplay.searchDebouncePending}
          isDraftsView={app.isDraftsFolder}
          isSentView={app.isSentFolder}
          isOutboxView={app.isOutboxView}
          scrollLocked={app.nav.readerOpen}
          onOpenNav={app.nav.openNav}
          onRefresh={app.mail.refresh}
          onSelectMessage={app.handleSelectMessage}
          onPrefetchMessage={app.handlePrefetchMessage}
          bulk={app.bulk}
          actions={app.actions}
        />
      </div>

      <MobileReaderOverlay open={app.nav.readerOpen} onClose={closeReader}>
        <MobileReaderScreen
          message={app.reader.message}
          loading={app.reader.loading}
          error={app.reader.error}
          accountId={app.mail.accountId}
          accountEmail={app.mail.currentAccount.email}
          folder={app.readerImapFolder}
          seen={app.selectedPreview?.seen ?? app.reader.message?.seen ?? true}
          flagged={app.selectedReaderFlags.flagged}
          favorite={app.selectedReaderFlags.favorite}
          outboxItem={app.selectedOutboxItem}
          scheduledSend={app.selectedScheduledSend}
          scheduledBusy={app.scheduledSends.loading}
          onBack={closeReader}
          bulkSelectionActive={app.bulk.selectionCount > 0}
          onAction={readerAction}
          onOutboxRetry={() => {
            const item = app.selectedOutboxItem;
            if (item) app.sendQueue.retry(item.id);
          }}
          onOutboxEdit={() => {
            const item = app.selectedOutboxItem;
            if (item) app.compose.openComposeFromOutbox(item);
          }}
          onOutboxDismiss={() => {
            const item = app.selectedOutboxItem;
            if (item) {
              app.sendQueue.dismiss(item.id);
              app.nav.backToInbox();
            }
          }}
          onScheduledSendNow={() => {
            const entry = app.selectedScheduledSend;
            if (!entry) return;
            void app.scheduledSends.sendNow(entry.id).then((ok) => {
              if (ok) app.nav.backToInbox();
            });
          }}
          onScheduledCancel={() => {
            const entry = app.selectedScheduledSend;
            if (!entry) return;
            void app.scheduledSends.cancel(entry.id).then((ok) => {
              if (ok) app.nav.backToInbox();
            });
          }}
          onRetry={() => app.reader.reload({ force: true })}
        />
      </MobileReaderOverlay>

      <MobileNavDrawer
        open={app.nav.navOpen}
        accounts={app.orderedAccounts}
        accountId={app.mail.accountId}
        folder={app.mail.folder}
        navFolders={app.mail.navFolders}
        extraNavFolders={app.mail.extraNavFolders}
        virtualEntries={app.virtualNavEntries}
        onClose={app.nav.closeNav}
        onSelectAccount={app.handleSelectAccount}
        onSelectFolder={app.handleSelectFolder}
        onOpenSettings={(focus) => app.handleOpenSettings(focus ?? "navigation")}
        onAddAccount={() => app.setConnectModalOpen(true)}
        foldersLoading={app.mail.foldersLoading}
        settingsVersion={app.settingsVersion + app.mail.accountSettingsVersion}
      />

      {!app.nav.readerOpen && !app.composeOpen ? (
        <MobileComposeFab onClick={() => app.compose.openCompose("new")} />
      ) : null}

      <ComposeDrawer
        fullscreen
        session={app.compose.composeSession}
        accounts={app.orderedAccounts}
        accountEmail={app.compose.composeAccount?.email ?? app.mail.currentAccount.email}
        settingsVersion={app.settingsVersion + app.mail.accountSettingsVersion}
        onUpdateDraft={app.compose.updateComposeDraft}
        onSaveDraft={app.compose.handleSaveComposeDraft}
        onDiscardDraft={app.compose.handleDiscardComposeDraft}
        onCloseSession={app.compose.closeComposeSession}
        onEnqueue={app.compose.handleComposeEnqueue}
        onScheduleSend={app.compose.handleScheduleCompose}
        onChangeComposeAccount={app.compose.changeComposeAccount}
        sending={
          app.compose.sendHandoffSessionId != null &&
          app.compose.sendHandoffSessionId === app.compose.composeSession?.id
        }
      />

      <MailMoveFolderDialog
        open={app.actions.moveDialogOpen || app.bulk.moveDialogOpen}
        folders={app.moveFolders}
        currentFolder={app.mail.folder}
        title={
          app.bulk.moveDialogOpen && app.bulk.pendingActionCount > 1
            ? `Déplacer ${app.bulk.pendingActionCount} messages`
            : undefined
        }
        onSelect={(target) => {
          if (app.bulk.moveDialogOpen) {
            void app.bulk.confirmMoveMessages(target);
          } else {
            void app.actions.confirmMove(target);
          }
        }}
        onClose={() => {
          app.actions.closeMoveDialog();
          app.bulk.closeMoveDialog();
        }}
      />

      <SnoozeModal
        open={app.actions.snoozeDialog != null || app.bulk.snoozeDialog != null}
        subject={app.bulk.snoozeDialog?.subject ?? app.actions.snoozeDialog?.subject}
        busy={app.actions.snoozeBusy || app.bulk.snoozeBusy}
        onConfirm={(untilIso) => {
          if (app.bulk.snoozeDialog != null) {
            void app.bulk.confirmSnooze(untilIso);
          } else {
            void app.actions.confirmSnooze(untilIso);
          }
        }}
        onClose={() => {
          app.actions.closeSnoozeDialog();
          app.bulk.closeSnoozeDialog();
        }}
      />

      <MailAccountSettingsModal
        open={app.settingsOpen}
        account={app.mail.currentAccount}
        folders={app.mail.folders}
        focusSection={app.settingsFocus}
        disconnectBusy={app.disconnecting}
        onClose={() => {
          app.setSettingsOpen(false);
          app.setSettingsFocus("navigation");
        }}
        onSaved={app.handleAccountSettingsSaved}
        onAccountUpdated={app.handleAccountUpdated}
        onDefaultChanged={app.handleDefaultAccountChanged}
        onDisconnect={(accountId) => void app.handleDisconnectAccount(accountId)}
        onPasswordUpdated={() => void app.mail.reloadAccounts()}
        onPhotoUpdated={() => void app.mail.reloadAccounts()}
        onCachePurged={() => {
          app.nav.backToInbox();
          notifyMailStorageStatsRefresh();
        }}
        onCacheRefreshed={(options) => {
          if (options?.force) {
            void app.mail.reloadFolders(true);
          } else {
            void app.mail.syncInBackground();
          }
          notifyMailStorageStatsRefresh();
        }}
      />

      <AccountConnectModal
        open={app.connectModalOpen}
        onClose={() => app.setConnectModalOpen(false)}
        onConnected={(account) => void app.handleAccountConnected(account)}
      />

      <CoffreDestFolderPickerModal
        open={app.bulk.attachPicker?.mode === "coffre"}
        title={`Enregistrer ${app.bulk.attachPicker?.targets.length ?? 0} message(s) dans le Coffre`}
        confirmLabel="Enregistrer les PJ ici"
        onClose={() => app.bulk.closeAttachPicker()}
        onConfirm={(folderPath, zone) => app.bulk.confirmBulkCoffre(folderPath, zone)}
      />
      <CoffreDestFolderPickerModal
        open={app.bulk.emlArchive != null}
        filename={
          app.bulk.emlArchive?.targets.length === 1
            ? `${(app.bulk.emlArchive.targets[0]?.subject || "Message").trim() || "Message"}.eml`
            : undefined
        }
        title={
          (app.bulk.emlArchive?.targets.length ?? 0) > 1
            ? `Archiver ${app.bulk.emlArchive?.targets.length} messages dans le Coffre`
            : "Archiver dans le Coffre"
        }
        confirmLabel="Archiver ici"
        onClose={() => app.bulk.closeEmlArchive()}
        onConfirm={(folderPath, zone) => app.bulk.confirmArchiveToCoffre(folderPath, zone)}
      />
      <BulkAttachmentProgressModal open={app.bulk.attachBusy} progress={app.bulk.attachProgress} />

      {app.mail.accountId != null && folderStorageEnabled ? (
        <MailFolderStorageDialog
          open={app.folderStorageOpen}
          accountId={app.mail.accountId}
          folder={app.mail.folder}
          folderLabel={app.folderTitle}
          uids={app.inboxListMessages.map((m) => m.uid).filter((uid) => uid > 0)}
          onClose={() => app.setFolderStorageOpen(false)}
        />
      ) : null}
      {bootOverlay}
    </MobileAppShell>
  );
}
